import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const FormField = ({ field, value, onChange, formData }) => {
  const getFieldValue = (fieldData, parentId) => {
    if (!parentId) {
      // For top-level fields, return the direct value
      return value || '';
    }
    
    // For nested fields, traverse the object structure
    let currentValue = value;
    if (!currentValue) return '';
    
    if (fieldData.type === 'group') {
      return currentValue;
    }
    
    return currentValue[fieldData.id] || '';
  };

  const updateFieldValue = (newValue, fieldData, parentId) => {
    if (!parentId) {
      // For top-level fields, pass the value directly
      onChange(newValue);
      return;
    }

    // For nested fields in a group
    const updatedValue = { ...value };
    updatedValue[fieldData.id] = newValue;
    onChange(updatedValue);
  };

  const handleChange = (e) => {
    const { type, checked, value: inputValue } = e.target;
    let newValue;

    if (type === 'checkbox') {
      console.log('Checkbox change:', {
        fieldId: field.id,
        inputValue,
        currentValue: value,
        checked
      });
      
      // For checkboxes, we need to maintain an array of selected values
      newValue = handleCheckboxChange(inputValue, value, checked);
    } else if (type === 'radio') {
      // Handle both boolean and string radio values
      newValue = inputValue === 'true' ? true :
                 inputValue === 'false' ? false :
                 inputValue;
    } else {
      newValue = inputValue;
    }

    onChange(newValue);
  };

  const handleCheckboxChange = (checkboxValue, currentValue, isChecked) => {
    // Initialize as array if not already
    const values = Array.isArray(currentValue) ? [...currentValue] : [];
    
    // If checked, add to array if not already there
    if (isChecked) {
      if (!values.includes(checkboxValue)) {
        values.push(checkboxValue);
      }
    } 
    // If unchecked, remove from array
    else {
      const index = values.indexOf(checkboxValue);
      if (index > -1) {
        values.splice(index, 1);
      }
    }
    
    console.log('Checkbox values after change:', {
      fieldId: field.id,
      values,
      isChecked,
      checkboxValue
    });
    
    return values;
  };

  const handleAddDynamicItem = () => {
    const currentItems = Array.isArray(value) ? value : [];
    onChange([...currentItems, {}]);
  };

  const handleRemoveDynamicItem = (index) => {
    const currentItems = Array.isArray(value) ? value : [];
    onChange(currentItems.filter((_, i) => i !== index));
  };

  const handleDynamicItemChange = (index, newValue) => {
    const currentItems = Array.isArray(value) ? value : [];
    const updatedItems = [...currentItems];
    updatedItems[index] = newValue;
    onChange(updatedItems);
  };

  const renderField = (fieldData, parentId = '') => {
    const fieldValue = getFieldValue(fieldData, parentId);

    switch (fieldData.type) {
      case 'dynamic':
        const items = Array.isArray(value) ? value : [];
        const minItems = fieldData.minItems || 0;
        
        return (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="relative p-4 rounded-lg border" style={{ backgroundColor: 'var(--succession-bg-secondary)', borderColor: 'var(--succession-border)' }}>
                <div className="absolute top-2 right-2">
                  {items.length > minItems && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDynamicItem(index)}
                      className="hover:opacity-80"
                      style={{ color: 'var(--succession-error)' }}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <FormField
                  field={fieldData.template}
                  value={item}
                  onChange={(newValue) => handleDynamicItemChange(index, newValue)}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddDynamicItem}
              className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ backgroundColor: 'var(--succession-primary)', focusRingColor: 'var(--succession-primary)' }}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {fieldData.buttonText || 'Add Item'}
            </button>
          </div>
        );

      case 'group':
        return (
          <div key={fieldData.id} className="space-y-4">
            <h3 className="text-lg font-medium" style={{ color: 'var(--succession-text-primary)' }}>{fieldData.label}</h3>
            <div className="space-y-4">
              {fieldData.fields.map(subField => (
                <div key={subField.id}>
                  <FormField
                    field={subField}
                    value={fieldValue?.[subField.id]}
                    onChange={(newValue) => {
                      const updatedGroupValue = {
                        ...fieldValue,
                        [subField.id]: newValue
                      };
                      onChange(updatedGroupValue);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <input
            type={fieldData.type}
            id={fieldData.id}
            name={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            placeholder={fieldData.placeholder}
            required={fieldData.required}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
            style={{ borderColor: 'var(--succession-border)', backgroundColor: 'var(--succession-bg-primary)', color: 'var(--succession-text-primary)' }}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={fieldData.id}
            name={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            placeholder={fieldData.placeholder}
            required={fieldData.required}
            rows={4}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
            style={{ borderColor: 'var(--succession-border)', backgroundColor: 'var(--succession-bg-primary)', color: 'var(--succession-text-primary)' }}
          />
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            <div className="text-sm font-medium mb-2" style={{ color: 'var(--succession-text-primary)' }}>
              {fieldData.label}
              {fieldData.required && <span style={{ color: 'var(--succession-error)' }}>*</span>}
            </div>
            {fieldData.options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${fieldData.id}-${option.value}`}
                  value={option.value}
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded"
                  style={{ color: 'var(--succession-primary)', borderColor: 'var(--succession-border)' }}
                />
                <label
                  htmlFor={`${fieldData.id}-${option.value}`}
                  className="ml-2 block text-sm"
                  style={{ color: 'var(--succession-text-primary)' }}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            <div className="text-sm font-medium mb-2" style={{ color: 'var(--succession-text-primary)' }}>
              {fieldData.label}
              {fieldData.required && <span style={{ color: 'var(--succession-error)' }}>*</span>}
            </div>
            {fieldData.options.map((option) => {
              const optionValue = option.value === 'true' ? true : option.value === 'false' ? false : option.value;
              return (
                <div key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    id={`${fieldData.id}-${option.value}`}
                    name={fieldData.id}
                    value={option.value}
                    checked={value === optionValue}
                    onChange={handleChange}
                    className="h-4 w-4"
                    style={{ color: 'var(--succession-primary)', borderColor: 'var(--succession-border)' }}
                  />
                  <label
                    htmlFor={`${fieldData.id}-${option.value}`}
                    className="ml-2 block text-sm"
                    style={{ color: 'var(--succession-text-primary)' }}
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            id={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            className="shadow-sm block w-full sm:text-sm rounded-md"
            style={{ borderColor: 'var(--succession-border)', backgroundColor: 'var(--succession-bg-primary)', color: 'var(--succession-text-primary)' }}
          />
        );

      default:
        return null;
    }
  };

  const shouldDisplay = () => {
    if (!field.conditional) return true;
    
    const { field: conditionalField, value: expectedValue, operator } = field.conditional;
    
    // Special case for securityDetails field
    if (field.id === 'securityDetails') {
      // Show securityDetails for both "other" and "none" selections
      const securitization = formData.paymentTerms?.securitization;
      console.log('Security details check:', {
        securitization,
        shouldShow: securitization === 'other' || securitization === 'none'
      });
      return securitization === 'other' || securitization === 'none';
    }
    
    // Special case for compensationDetails field
    if (field.id === 'compensationDetails') {
      const ownerCompensation = formData.clientTransition?.ownerCompensation;
      console.log('Compensation details check:', {
        ownerCompensation,
        shouldShow: ownerCompensation !== 'none' && ownerCompensation !== undefined
      });
      return ownerCompensation !== 'none' && ownerCompensation !== undefined;
    }
    
    // Special case for otherApprovals field
    if (field.id === 'otherApprovals') {
      const regulatoryApprovals = formData.legalRegulatory?.regulatoryApprovals;
      console.log('Other approvals check:', {
        regulatoryApprovals,
        shouldShow: Array.isArray(regulatoryApprovals) && regulatoryApprovals.includes('other')
      });
      return Array.isArray(regulatoryApprovals) && regulatoryApprovals.includes('other');
    }
    
    // Special case for nonCompete fields
    if (field.id === 'nonCompeteDuration' || field.id === 'nonCompeteGeography') {
      const nonCompete = formData.legalRegulatory?.nonCompete;
      console.log('Non-compete field check:', {
        nonCompete,
        fieldId: field.id,
        shouldShow: nonCompete === 'yes'
      });
      return nonCompete === 'yes';
    }
    
    // Special case for ROFR fields
    if (field.id === 'rofrNegotiationPeriod' || field.id === 'rofrMatchPeriod') {
      const includeROFR = formData.rightOfFirstRefusal?.includeROFR;
      console.log('ROFR field check:', {
        includeROFR,
        fieldId: field.id,
        shouldShow: includeROFR === 'yes'
      });
      return includeROFR === 'yes';
    }
    
    // Special case for insurance amount
    if (field.id === 'insuranceAmount') {
      const keyPersonInsurance = formData.additionalProvisions?.keyPersonInsurance;
      console.log('Insurance amount check:', {
        keyPersonInsurance,
        shouldShow: keyPersonInsurance === 'yes'
      });
      return keyPersonInsurance === 'yes';
    }
    
    // Check if we're in the triggerEvents section with field-only conditionals 
    const isTriggerEventConditional = 
      field.id === 'disabilityDefinition' || 
      field.id === 'retirementDefinition';
    
    if (isTriggerEventConditional) {
      const sectionId = 'triggerEvents';
      const fieldId = conditionalField; // No need to split, use the whole value
      
      console.log('TriggerEvents direct check:', {
        field: field.id,
        sectionId,
        fieldId, 
        actualValue: formData[sectionId]?.[fieldId],
        expectedValue
      });
      
      // Direct handling for disability definition
      if (field.id === 'disabilityDefinition') {
        return Array.isArray(formData.triggerEvents?.includeDisability) && 
               formData.triggerEvents.includeDisability.includes('disability');
      }
      
      // Direct handling for retirement definition
      if (field.id === 'retirementDefinition') {
        return Array.isArray(formData.triggerEvents?.includeRetirement) && 
               formData.triggerEvents.includeRetirement.includes('retirement');
      }
    }
    
    // Original conditional logic for other fields
    // Parse conditionalField - either section.field format or just field
    let section, fieldId;
    if (conditionalField.includes('.')) {
      [section, fieldId] = conditionalField.split('.');
    } else {
      // If there's no dot, use current section and whole value as field
      section = field.id.includes('Definition') ? 'triggerEvents' : 
               field.id.includes('Multiple') || field.id.includes('Percentage') || 
               field.id.includes('Premium') || field.id.includes('Method') || 
               field.id.includes('Value') || field.id.includes('Formula') ? 'valuationMethodology' : 
               field.id.includes('Period') ? 'rightOfFirstRefusal' : 
               field.id.includes('Partner') ? 'catastrophicPartner' : 
               field.id.includes('Insurance') ? 'additionalProvisions' : 
               field.id.includes('Premium') || field.id === 'securityDetails' ? 'paymentTerms' : 
               field.id.includes('Compensation') ? 'clientTransition' :
               field.id.includes('Approvals') ? 'legalRegulatory' :
               field.id.includes('Compete') ? 'legalRegulatory' :
               field.id.includes('ROFR') ? 'rightOfFirstRefusal' :
               'unknown';
      fieldId = conditionalField;
    }
    
    let actualValue = formData[section]?.[fieldId];
    
    // Debug logging
    console.log(`Conditional check for ${field.id}:`, {
      field: field.id,
      conditionalField,
      section,
      fieldId,
      actualValue: JSON.stringify(actualValue),
      expectedValue: JSON.stringify(expectedValue),
      operator,
      formDataSection: JSON.stringify(formData[section])
    });
    
    // If actualValue is undefined or null, return false since we need a value to compare
    if (actualValue === undefined || actualValue === null) return false;
    
    // Special case for checkbox values which are arrays
    if (Array.isArray(actualValue)) {
      console.log(`Array check for ${field.id}:`, {
        actualValue,
        expectedValue,
        includes: actualValue.includes(expectedValue)
      });
      // Check if the expected value is in the array
      const result = actualValue.includes(expectedValue);
      // Apply "not" operator if specified
      return operator === 'not' ? !result : result;
    }
    
    // Convert string values to appropriate types for comparison
    const processedActualValue = actualValue === 'true' ? true :
                                 actualValue === 'false' ? false :
                                 actualValue;
    
    const processedExpectedValue = expectedValue === 'true' ? true :
                                   expectedValue === 'false' ? false :
                                   expectedValue;
    
    // For boolean comparisons, be extra careful
    if (typeof processedExpectedValue === 'boolean') {
      // Convert to boolean for comparison
      const actualBool = processedActualValue === true || processedActualValue === 'true';
      const result = actualBool === processedExpectedValue;
      // Apply "not" operator if specified
      return operator === 'not' ? !result : result;
    }
    
    // For string comparisons
    if (typeof processedActualValue === 'string' && typeof processedExpectedValue === 'string') {
      const result = processedActualValue.toLowerCase() === processedExpectedValue.toLowerCase();
      // Apply "not" operator if specified
      return operator === 'not' ? !result : result;
    }
    
    // Default comparison
    const result = processedActualValue === processedExpectedValue;
    // Apply "not" operator if specified
    return operator === 'not' ? !result : result;
  };

  if (!shouldDisplay()) return null;

  return (
    <div className="space-y-1">
      {field.type !== 'group' && field.type !== 'checkbox' && field.type !== 'radio' && field.type !== 'dynamic' && (
        <label htmlFor={field.id} className="block text-sm font-medium" style={{ color: 'var(--succession-text-primary)' }}>
          {field.label}
          {field.required && <span style={{ color: 'var(--succession-error)' }}>*</span>}
        </label>
      )}
      {field.helpText && (
        <p className="text-sm" style={{ color: 'var(--succession-info)' }}>{field.helpText}</p>
      )}
      {renderField(field)}
    </div>
  );
};

export default FormField; 